import { langFallback$Fetch } from '../../../../../lang/utils/langfetch'
import { LocationSuggestionCampsite } from '../domain/LocationSuggestions'
import { CampsiteResponse, campsiteResponseTypeSchema } from '../schemas'
import {
  LocationSuggestionsFetchError,
  LocationSuggestionsValidationError,
} from './LocationSuggestionsError'

// Default timeout for search suggestions (10 seconds - should be fast)
const SEARCH_SUGGESTIONS_TIMEOUT = 10000

export default class FetchLocationCampsiteSuggestions {
  async fetch(
    query: string,
    langCode: string,
  ): Promise<LocationSuggestionCampsite[]> {
    const url = `/search/predictive/campsite/?q=${query}`
    let response: CampsiteResponse[]

    // Step 1: Fetch the data
    try {
      response = await langFallback$Fetch<CampsiteResponse[]>(langCode, url, {
        timeout: SEARCH_SUGGESTIONS_TIMEOUT,
      })
    } catch (error: any) {
      // Silently return empty array for abort errors - they're intentional cancellations
      // This is especially common in predictive search when users type quickly
      if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
        return []
      }

      // Throw custom fetch error - don't log here, let the store handle logging to avoid duplicates
      throw new LocationSuggestionsFetchError(url, query, 'campsite', error)
    }

    // Step 2: Validate with Zod schema
    try {
      const data = campsiteResponseTypeSchema.array().parse(response)
      return this.getParsedCampsites(data) as LocationSuggestionCampsite[]
    } catch (error: any) {
      const validationError = new LocationSuggestionsValidationError(
        url,
        query,
        'campsite',
        response,
        error,
      )
      // Don't log here - let the store handle logging to avoid duplicates
      throw validationError
    }
  }

  private getParsedCampsites(campsites: CampsiteResponse[]) {
    return campsites.map((campsite) => ({
      ...campsite,
      countryCode: campsite.countryIso,
      inputValue: campsite.value,
      hierarchyPath: campsite.tokens[1],
      slug: this.getCampsiteSlugFromUrl(campsite.url) as string,
    }))
  }

  private getCampsiteSlugFromUrl(url: string) {
    return url.split('/').slice(0, -1).pop()
  }
}
